/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState, useEffect, useRef, useCallback} from "react";
import {useNavigate} from "react-router-dom";
import toast from "react-hot-toast";
import {
  MdDelete,
  MdAdd,
  MdSearch,
  MdChevronLeft,
  MdChevronRight,
} from "react-icons/md";
import ClientService from "../../../services/client.service";
import ConfirmModal from "../../../components/confirm-modal";

const Clients = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Estados para el Modal de Confirmación
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const timerRef = useRef(null);

  const load = useCallback(
    async (currentPage = page, currentSearch = search) => {
      setLoading(true);

      try {
        const res = await ClientService.admin.get({
          page: currentPage,
          withoutLoading: true,
          search: currentSearch,
        });

        // Reemplaza los datos en lugar de acumularlos
        const rows = res?.clients?.rows || [];
        const count = res?.clients?.count || 0;
        const pages = res?.clients?.totalPages || Math.ceil(count / 10) || 1;

        setData(rows);
        setTotalItems(count);
        setTotalPages(pages);
      } catch (error) {
        toast.error(
          typeof error === "string"
            ? error
            : error?.response?.data?.message ||
                error?.message ||
                "Error al cargar clientes",
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    load(page, search);
  }, [page]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    setPage(1);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => load(1, val), 500);
  };

  const openDeleteModal = (item, e) => {
    e.stopPropagation();
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;
    setLoadingDelete(true);
    try {
      await ClientService.admin.delete({user_id: selectedItem.id});
      toast.success("Se ha eliminado el cliente");
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
      load(page, search);
    } catch (error) {
      const errorMsg =
        typeof error === "string"
          ? error
          : error?.response?.data?.message ||
            error?.message ||
            "Error al eliminar";
      toast.error(errorMsg);
    } finally {
      setLoadingDelete(false);
    }
  };

  return (
    <div className="flex flex-col h-full flex-1 min-h-0 px-4 py-6 md:px-8">
      {/* Header & Buscador */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 shrink-0">
        <div className="relative w-full md:w-1/2 lg:w-1/3">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MdSearch size={20} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar clientes..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-[#1D3B8B] focus:border-transparent outline-none transition-all"
            value={search}
            onChange={handleSearch}
          />
        </div>

        <button
          onClick={() => navigate("/admin/clients/create")}
          className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-[#1D3B8B] text-white rounded-xl font-semibold shadow-md hover:bg-orange-600 transition-colors"
        >
          <MdAdd size={20} />
          <span>Nuevo Cliente</span>
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col flex-1 min-h-0 overflow-hidden">
        <div className="overflow-auto flex-1">
          <table className="w-full text-sm text-left text-gray-600 relative">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-b border-gray-100 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-6 py-4 font-semibold">COD</th>
                <th className="px-6 py-4 font-semibold">TARIFA</th>
                <th className="px-6 py-4 font-semibold">Nombre</th>
                <th className="px-6 py-4 font-semibold">Estado</th>
                <th className="px-6 py-4 font-semibold">Ciudad</th>
                <th className="px-6 py-4 font-semibold text-center">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading && (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-8 text-center text-[#1D3B8B] font-medium"
                  >
                    Cargando...
                  </td>
                </tr>
              )}

              {!loading && data.length === 0 && (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-8 text-center text-gray-400"
                  >
                    No se han encontrado registros
                  </td>
                </tr>
              )}

              {!loading &&
                data.map((item, index) => (
                  <tr
                    key={item.id || index}
                    className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                    onClick={() =>
                      navigate("/admin/clients/create", {state: {item}})
                    }
                  >
                    <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                      {item.code || "-"}
                    </td>
                    <td className="px-6 py-4 text-green-600 font-medium">
                      $ {item.tarifa || "0.00"}
                    </td>
                    <td className="px-6 py-4 font-bold text-[#1D3B8B]">
                      {item.full_name}
                    </td>
                    <td className="px-6 py-4">{item.state?.name || "-"}</td>
                    <td className="px-6 py-4">{item.city?.name || "-"}</td>
                    <td className="px-6 py-4 flex justify-center">
                      <button
                        type="button"
                        onClick={(e) => openDeleteModal(item, e)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar cliente"
                      >
                        <MdDelete size={22} />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Barra de Paginación */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50 gap-3 shrink-0">
          <span className="text-xs text-gray-500">
            Mostrando página{" "}
            <span className="font-semibold text-gray-800">{page}</span> de{" "}
            <span className="font-semibold text-gray-800">{totalPages}</span> (
            {totalItems} registros en total)
          </span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <MdChevronLeft size={18} />
              Anterior
            </button>

            <span className="px-3 py-1 text-xs font-semibold text-[#1D3B8B] bg-blue-50 rounded-lg">
              {page}
            </span>

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Siguiente
              <MdChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="¿Desea eliminar el cliente?"
        message="Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
        loading={loadingDelete}
        onConfirm={handleConfirmDelete}
        onClose={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
};

export default Clients;
